import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Key, Save, Loader2, Wand2, AlertTriangle, Copy, Check, Lock, Unlock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

interface XPubEntry {
  key: string;
  value: string;
  label: string;
  blockchain: string;
}

const BLOCKCHAIN_OPTIONS: XPubEntry[] = [
  { key: "xpub_ethereum", label: "Ethereum / EVM (ETH, BSC, Polygon, Arbitrum...)", value: "", blockchain: "ethereum" },
  { key: "xpub_evm", label: "EVM مشترك (احتياطي لكل شبكات EVM)", value: "", blockchain: "evm" },
  { key: "xpub_bitcoin", label: "Bitcoin (BTC)", value: "", blockchain: "bitcoin" },
  { key: "xpub_litecoin", label: "Litecoin (LTC)", value: "", blockchain: "litecoin" },
  { key: "xpub_tron", label: "Tron (TRC20)", value: "", blockchain: "tron" },
  { key: "xpub_bsc", label: "BSC (BEP20) - مخصص", value: "", blockchain: "bsc" },
  
  { key: "xpub_polygon", label: "Polygon - مخصص", value: "", blockchain: "polygon" },
  { key: "xpub_solana", label: "Solana (SOL)", value: "", blockchain: "solana" },
];

const AdminXPubSettings = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [entries, setEntries] = useState<XPubEntry[]>([]);
  const [generating, setGenerating] = useState<string | null>(null);
  const [mnemonicDialog, setMnemonicDialog] = useState<{ open: boolean; mnemonic: string; blockchain: string }>({
    open: false,
    mnemonic: "",
    blockchain: "",
  });
  const [copied, setCopied] = useState(false);
  const [lockedKeys, setLockedKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchXPubs();
  }, []);

  const fetchXPubs = async () => {
    try {
      const { data, error } = await supabase
        .from("platform_settings")
        .select("setting_key, setting_value")
        .like("setting_key", "xpub_%");

      if (error) throw error;

      const result = BLOCKCHAIN_OPTIONS.map((opt) => {
        const found = (data || []).find((d) => d.setting_key === opt.key);
        return { ...opt, value: found?.setting_value || "" };
      });

      // Auto-lock entries that already have values
      const locked = new Set<string>();
      result.forEach(e => { if (e.value.trim()) locked.add(e.key); });
      setLockedKeys(locked);

      setEntries(result);
    } catch (error) {
      console.error("Error fetching xPub settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key: string, value: string) => {
    setEntries((prev) =>
      prev.map((e) => (e.key === key ? { ...e, value } : e))
    );
  };

  const handleGenerate = async (entry: XPubEntry) => {
    setGenerating(entry.key);
    try {
      const { data, error } = await supabase.functions.invoke("generate-xpub", {
        body: { blockchain: entry.blockchain },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      if (data?.xpub) {
        handleChange(entry.key, data.xpub);
        toast({
          title: "تم التوليد ✅",
          description: `تم توليد xPub لـ ${entry.label} بنجاح`,
        });

        // Show mnemonic warning dialog
        if (data.mnemonic) {
          setMnemonicDialog({
            open: true,
            mnemonic: data.mnemonic,
            blockchain: entry.label,
          });
        }
      }
    } catch (error: any) {
      console.error("Generate xPub error:", error);
      toast({
        title: "خطأ في التوليد",
        description: error.message || "فشل في توليد مفتاح xPub",
        variant: "destructive",
      });
    } finally {
      setGenerating(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const entry of entries) {
        if (!entry.value.trim()) continue;

        const { data: existing } = await supabase
          .from("platform_settings")
          .select("id")
          .eq("setting_key", entry.key)
          .maybeSingle();

        if (existing) {
          const { error } = await supabase
            .from("platform_settings")
            .update({ setting_value: entry.value.trim(), updated_at: new Date().toISOString() })
            .eq("setting_key", entry.key);
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from("platform_settings")
            .insert({
              setting_key: entry.key,
              setting_value: entry.value.trim(),
              description: `xPub key for ${entry.label}`,
            });
          if (error) throw error;
        }
      }

      toast({
        title: "تم الحفظ",
        description: "تم تحديث مفاتيح xPub بنجاح",
      });
    } catch (error) {
      console.error("Error saving xPub settings:", error);
      toast({
        title: "خطأ",
        description: "فشل في حفظ المفاتيح",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCopyMnemonic = async () => {
    await navigator.clipboard.writeText(mnemonicDialog.mnemonic);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <Card className="glass">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="glass">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-foreground">
            <Key className="w-5 h-5 text-primary" />
            إدارة مفاتيح xPub (HD Wallet)
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            أدخل مفتاح xPub يدوياً أو اضغط "توليد" لإنشائه تلقائياً عبر Tatum API.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {entries.map((entry) => {
            const isLocked = lockedKeys.has(entry.key);
            return (
              <div key={entry.key} className="space-y-1.5">
                <Label className="text-foreground text-sm font-medium flex items-center gap-2">
                  {entry.label}
                  {isLocked && <Lock className="w-3.5 h-3.5 text-green-500" />}
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={isLocked ? '••••••••••••••••••••' : entry.value}
                    onChange={(e) => handleChange(entry.key, e.target.value)}
                    className="bg-input border-border text-foreground font-mono text-xs flex-1"
                    dir="ltr"
                    placeholder="xpub6..."
                    disabled={isLocked}
                  />
                  {!isLocked && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleGenerate(entry)}
                      disabled={generating === entry.key}
                      className="shrink-0"
                    >
                      {generating === entry.key ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Wand2 className="w-4 h-4" />
                      )}
                      <span className="mr-1 hidden sm:inline">توليد</span>
                    </Button>
                  )}
                </div>
                {isLocked && <p className="text-[10px] text-green-500/70">🔒 مقفل - تم التعيين مسبقاً</p>}
              </div>
            );
          })}

          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full gradient-primary text-primary-foreground mt-4"
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                جاري الحفظ...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Save className="w-4 h-4" />
                حفظ مفاتيح xPub
              </span>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Mnemonic Warning Dialog */}
      <Dialog open={mnemonicDialog.open} onOpenChange={(open) => setMnemonicDialog((p) => ({ ...p, open }))}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              ⚠️ احفظ عبارة الاسترداد (Mnemonic)
            </DialogTitle>
            <DialogDescription className="text-right">
              هذه العبارة هي المفتاح الرئيسي للمحفظة. احفظها في مكان آمن جداً ولا تشاركها مع أحد.
              <strong className="block mt-2 text-destructive">لن تظهر هذه العبارة مرة أخرى!</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="bg-muted p-3 rounded-lg border border-border">
            <p className="font-mono text-xs text-foreground leading-relaxed break-all dir-ltr" dir="ltr">
              {mnemonicDialog.mnemonic}
            </p>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={handleCopyMnemonic} className="flex items-center gap-2">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? "تم النسخ" : "نسخ"}
            </Button>
            <Button onClick={() => setMnemonicDialog((p) => ({ ...p, open: false }))}>
              لقد حفظتها
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminXPubSettings;
